import { useSyncExternalStore } from 'react';
import type { HintsDto } from '@werft/contracts';
import { t } from '../../i18n/index.js';
import { api, newOpId } from '../../platform/api.js';
import { toast } from '../../platform/toast.js';

/**
 * Briefing „Arbeitsfreude“ AF08: situative Vorschläge nach einem relevanten EIGENEN Ereignis.
 *
 * Regeln (bewusst hier an einer Stelle):
 * - Höchstens ein Vorschlag zugleich; ein neuer ersetzt einen älteren (der ältere ist der veraltete).
 * - Nicht dazwischenschieben: solange gezogen wird, ein Fenster/Popover offen ist oder getippt wird, wartet der
 *   Vorschlag – höchstens eine Minute, danach verfällt er.
 * - Kein Fokus-Diebstahl, keine Sperre: der Vorschlag erscheint in einer ruhigen Zone und wird vorgelesen (status).
 * - Vor dem Zeigen und vor der Aktion wird der Anlass erneut geprüft (`valid`); ohne gültige Aktion kein Vorschlag.
 * - Beantwortete oder abgelehnte Vorschläge mit `key` kommen nicht wieder (Zustand je Person auf dem Server,
 *   `/me/hints`); „Solche Hinweise ausschalten“ gilt für die Art; Produkttipps insgesamt im Profil.
 * - Ausgelöst wird nur aus Antworten auf eigene Änderungen dieses Tabs (Ausgangskorb) – Neuladen, Wiederverbinden
 *   und ein zweiter Tab erzeugen keine Hinweise.
 * Technische Fehler laufen weiter über die Hinweise unten (toast) und sind nicht abschaltbar.
 */
export interface Hint {
  /** Art (für „Solche Hinweise ausschalten“), z. B. `unblocked`, `subtasks`, `pin-page` */
  kind: string;
  /** Dauerhafter Schlüssel: einmal beantwortet oder abgelehnt → nicht wieder (z. B. `pin-page:<id>`) */
  key?: string;
  /** Texte werden erst beim Zeigen gebildet (Texte der nachgeladenen Teile) */
  text: () => string;
  action: { label: () => string; run: () => unknown };
  /** Anlass noch gegeben? Vor dem Zeigen, regelmäßig während des Zeigens und vor der Aktion */
  valid?: () => boolean | Promise<boolean>;
}

export interface ActiveHint extends Hint {
  id: number;
}

const WAIT_MS = 60_000;
const SHOW_MS = 25_000;

let active: ActiveHint | null = null;

/** Aufträge aus einer Vorschlags-Aktion an eine Ansicht, die erst noch erscheint (Strukturhilfe, Aufgabe aus Thema) */
export const requests = { structure: null as string | null, topicTask: null as string | null };
let seq = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

/* Zustand je Person (einmal je Sitzung geladen; nach Änderungen lokal mitgeführt) */
let states: Promise<HintsDto> | null = null;
const loadStates = () => (states ??= api.get<HintsDto>('/api/me/hints').catch((): HintsDto => ({ tips: true, states: {} })));

export function hintStates(): Promise<HintsDto> {
  return loadStates();
}

/** Zustand setzen (null = vergessen). `_tips` = alle Produkttipps; `kind:<art>` = eine Art */
export async function setHintState(key: string, state: 'done' | 'dismissed' | 'off' | null): Promise<HintsDto> {
  const r = await api.put<HintsDto>('/api/me/hints', { opId: newOpId(), key, state });
  states = Promise.resolve(r);
  return r;
}

export async function resetHints(): Promise<HintsDto> {
  const r = await api.post<HintsDto>('/api/me/hints/reset', { opId: newOpId() });
  states = Promise.resolve(r);
  return r;
}

/** Nach Abmelden/Anmelden einer anderen Person neu laden */
export function forgetHintStates() {
  states = null;
  close();
}

/* Nicht dazwischenschieben */
let pointerDown = false;
if (typeof window !== 'undefined') {
  window.addEventListener('pointerdown', () => (pointerDown = true), true);
  window.addEventListener('pointerup', () => (pointerDown = false), true);
  window.addEventListener('pointercancel', () => (pointerDown = false), true);
}

function busy(): boolean {
  if (pointerDown) return true;
  if (document.querySelector('[role="dialog"], [role="alertdialog"], [role="menu"]')) return true;
  const a = document.activeElement as HTMLElement | null;
  if (!a) return false;
  if (a.isContentEditable || a.tagName === 'TEXTAREA' || a.tagName === 'SELECT') return true;
  return a.tagName === 'INPUT' && !['checkbox', 'radio', 'button', 'submit'].includes((a as HTMLInputElement).type);
}

const check = async (h: Hint) => {
  try {
    return h.valid ? await h.valid() : true;
  } catch {
    return false;
  }
};

/** Vorschlag anmelden. Ob und wann er erscheint, entscheiden die Regeln oben. */
export async function suggest(h: Hint): Promise<void> {
  const s = await loadStates();
  if (!s.tips || s.states[`kind:${h.kind}`] === 'off' || (h.key && s.states[h.key])) return;
  const until = Date.now() + WAIT_MS;
  const id = ++seq;
  pendingId = id;
  while (busy()) {
    if (Date.now() > until || pendingId !== id) return;
    await new Promise((r) => setTimeout(r, 700));
  }
  if (pendingId !== id || !(await check(h))) return;
  active = { ...h, id };
  emit();
  watch(id, h);
}
let pendingId = 0;

/** Während des Zeigens: veraltete Vorschläge verschwinden; nach einer Weile ohne Bedienung auch */
let held = false;
function watch(id: number, h: Hint) {
  let shown = 0;
  const tick = async () => {
    if (active?.id !== id) return;
    shown += 3000;
    if (!held && shown >= SHOW_MS) return close(id);
    if (!(await check(h))) return close(id);
    setTimeout(() => void tick(), 3000);
  };
  setTimeout(() => void tick(), 3000);
}

/** Zeiger auf dem Vorschlag oder Fokus darin: er bleibt stehen */
export function holdHint(on: boolean) {
  held = on;
}

export function close(id?: number) {
  if (!active || (id !== undefined && active.id !== id)) return;
  active = null;
  held = false;
  emit();
}

/** Antworten auf den Vorschlag */
export async function answer(how: 'act' | 'later' | 'off') {
  const h = active;
  if (!h) return;
  close();
  if (how === 'off') return void setHintState(`kind:${h.kind}`, 'off').catch(() => {});
  if (h.key) void setHintState(h.key, how === 'act' ? 'done' : 'dismissed').catch(() => {});
  if (how !== 'act') return;
  if (await check(h)) h.action.run();
  else toast(t('hints.stale'));
}

export function useActiveHint(): ActiveHint | null {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    () => active,
  );
}
